import React, {
	type PropsWithChildren,
	useCallback,
	useEffect,
	useId,
	useMemo,
	useRef,
	useState,
} from 'react';

import { useIntl } from 'react-intl';

import { getDocument } from '@atlaskit/browser-apis';
import { FlagsProvider } from '@atlaskit/flag';
import { useNotifyOpenLayerObserver } from '@atlaskit/layering/use-notify-open-layer-observer';
import { useIsFhsEnabled } from '@atlaskit/navigation-system/fhs-rollout';
import { fg } from '@atlaskit/platform-feature-flags';
// eslint-disable-next-line @atlaskit/design-system/no-emotion-primitives -- to be migrated to @atlaskit/primitives/compiled – go/akcss
import { Box, media, xcss } from '@atlaskit/primitives';
import UFOSegment from '@atlaskit/react-ufo/segment';
import { RelayEnvironmentProvider } from '@atlassian/relay-environment-provider';
import { KeyboardHighlightProvider } from '@atlassian/search-dialog/search-keyboard';
import { STATSIG_QUICK_FIND_LAYER } from '@atlassian/search-experiment/constants';

import { ProductKeys } from '../../common/constants/product-keys';
import { REFERRER_QUERY_PARAM } from '../../common/constants/query-params';
import { SearchExperience } from '../../common/constants/search-experience';
import {
	AppContextProvider,
	type AppContextProviderProps,
	useAppContext,
} from '../../common/ui/app-context';
import MessagesIntlProvider from '../../common/ui/message-intl-provider/messages-intl-provider';
import type { TextfieldProps } from '../../common/ui/quick-find/textfield';
import { getInitialQuery } from '../../common/ui/quick-find/textfield/input-skeleton';
import { WidthObserverProvider } from '../../common/ui/width-observer';
import { serveQF3PBannerExperimentWithoutExposure } from '../../common/utils/experiments';
import { serveJiraNINFpsNLQNavigation } from '../../common/utils/experiments/experiment-exports';
import {
	TrackImpressionProvider,
	PlacementPortalContainerProvider,
} from '../../common/utils/post-office-placement/placement-portal-context';
import type { QueryParams } from '../../common/utils/query-params/types';
import { isOnSearchRoute } from '../../common/utils/url';
import { QuickFindBannerPortal } from '../../controllers/quick-find/banner-placement/banner-portal';
import { QuickFindHeadlessBannerProvider } from '../../controllers/quick-find/banner-placement/headless-banner-provider';
import { QuickFindResults } from '../../controllers/quick-find/fetch-results';
import { IntentDetectionVitalStatsProvider } from '../../controllers/quick-find/intent-detection-vitalstats-context';
import { QuickFindPostQueryVitalStatsProvider } from '../../controllers/quick-find/quickfind-post-query-vitalstats-context';
import { usePrimaryProduct } from '../../controllers/store/bootstrap';
import {
	type BootstrapContainerProps,
	BootstrapStoreProvider as BootstrapProvider,
} from '../../controllers/store/bootstrap/bootstrap-store-provider';
import { useQuickFindActions, useQuickFindOpen } from '../../controllers/store/quick-find';
import { QuickFindStoreProvider } from '../../controllers/store/quick-find/quick-find-store-provider';
import { SearchStoreProvider } from '../../controllers/store/search/search-store-provider';

import { SearchDialogAsync } from './dialog/async';
import { useIntentRouterCleanup } from './dialog/intent-router/use-intent-router-cleanup';
import { useSearchDialogPreload } from './dialog/use-search-dialog-preload';
import { FiltersRow } from './filters-row';
import { SearchTextField } from './textfield';
import { useQuickFindOnNavigateWithWarmup } from './use-on-navigate-with-warmup';
import { useCloseManager } from './utils/use-close-manager';
import { usePreQueryDwellTimeAnalytics } from './utils/use-pre-query-dwell-time-analytics';
import { useSyncStates } from './utils/use-sync-states';

const textFieldBoxStyles = xcss({
	width: '100%',
	position: 'relative',
});

const minWidthStyles = xcss({
	left: '50%',
	transform: 'translate(-50%, 0)', // Anchor Quick Find input and dialog to the center of the viewport
	position: 'fixed',
	maxWidth: '780px', // To retain the same width as Quick Search
	zIndex: 'navigation',
	[media.above.lg]: {
		maxWidth: 'unset',
		position: 'relative',
	},
});

export type QuickFindComponentProps = {
	open?: boolean;
	setOpen?: (open: boolean) => void;
	postQueryDisabled?: boolean;
	textfieldComponent?: React.ComponentType<TextfieldProps>;
	isSSR?: boolean;
	PlacementPO?: () => JSX.Element;
	showFilters?: boolean;
	showFiltersRow?: boolean;
	searchRefreshStyling?: boolean;
	customPlaceholder?: string;
	onGhostTextGenerated?: (ghostText: string) => void;
	inputRef?: React.RefObject<HTMLInputElement>;
	/**
	 * Optional render function for product-specific pre-query content (e.g. connector promo).
	 * Called only during pre-query states. Receives `hasRecentItems` so the rendered
	 * component can adapt its layout.
	 */
	preQueryContent?: (hasRecentItems: boolean) => React.ReactElement;
	/** Predicted advanced-search URL emitted after the typing-debounce window; wire to `prefetchRoute`. Gated on `quick_find_typing_debounce_prefetch`. */
	onPrefetch?: (url: string) => void;
};

type QuickFindProps = BootstrapContainerProps & AppContextProviderProps & QuickFindComponentProps;

const QuickFindComponent = ({
	open,
	setOpen,
	postQueryDisabled,
	textfieldComponent,
	isSSR,
	PlacementPO,
	showFilters = true,
	showFiltersRow = false,
	searchRefreshStyling = false,
	customPlaceholder,
	onGhostTextGenerated,
	inputRef,
	preQueryContent,
	onPrefetch,
}: QuickFindComponentProps) => {
	const inputBoxRef = useRef<HTMLDivElement>(null);
	const dialogRef = useRef<HTMLDivElement>(null);
	const { isNav4Enabled, queryParams } = useAppContext();
	const [primaryProduct] = usePrimaryProduct();
	const internalInputRef = useRef<HTMLInputElement>(null);
	const effectiveInputRef = inputRef || internalInputRef;

	const intl = useIntl();
	const intentRouterEnabled = useMemo(
		() =>
			serveJiraNINFpsNLQNavigation({
				primaryProduct,
				fireLayerExposure: false, // Exposure is fired when a user calls the intent detection API
				locale: intl.locale,
			})?.cohort === 'test',
		[primaryProduct, intl.locale],
	);

	const [localQuery, setLocalQuery] = useState(() =>
		getInitialQuery((typeof queryParams.text === 'string' && queryParams.text) || ''),
	);

	const { setQuickFindOpen } = useQuickFindActions();

	const quickFindOpen = useQuickFindOpen();

	const dialogId = useId();

	const setMinWidthStyles =
		isNav4Enabled &&
		!textfieldComponent && // min-width styles is only supported with the default text field
		quickFindOpen;

	useSyncStates(quickFindOpen, setQuickFindOpen, open, setOpen);

	useCloseManager({ dialogRef, inputBoxRef });
	usePreQueryDwellTimeAnalytics();

	// Dispose intent detection state when QuickFind unmounts.
	useIntentRouterCleanup();

	const isFhsEnabled = useIsFhsEnabled();

	const handleOpenLayerObserverCloseSignal = useCallback(() => {
		if (!isFhsEnabled) {
			return;
		}

		setQuickFindOpen(false);
	}, [isFhsEnabled, setQuickFindOpen]);

	/**
	 * We need to notify the open layer observer when the quick find dialog is open.
	 * This is required for several navigation-system features to work correctly, such as:
	 * - Closing layers when the user resizes a layout slot (e.g. SideNav, Panel)
	 * - Ensuring correct layer stacking order when a popup is open within the TopNav or SideNav
	 *
	 * Ideally, the dialog would be using the ADS Popup component instead, which handles this internally
	 * among other benefits.
	 */
	useNotifyOpenLayerObserver({
		isOpen: isFhsEnabled && quickFindOpen,
		type: 'popup',
		onClose: handleOpenLayerObserverCloseSignal,
	});

	const isPOBannerPlacementforQFEnabled = serveQF3PBannerExperimentWithoutExposure();
	const isHeadlessQuickFindBannerEnabled = fg('post-office_enable_search-quick-find-headless');
	const document = getDocument();

	// Store the trackImpression callback provided by the PostOffice placement's onMessageViewed.
	// This is called later by OAuthContainer when the banner becomes visible in search results.
	const trackImpressionRef = useRef<(() => void) | null>(null);
	const onBannerVisible = useCallback(() => {
		trackImpressionRef.current?.();
	}, []);

	// Create a detached DOM node for the RML quick-find placement portal.
	// The placement component is mounted early (via createPortal) so its fetch starts in parallel with search, since requests are only made on placement mount.
	// When results render, <PlacementSlot> moves this node into the relevant position in the results list.
	const bannerPortalContainerRef = useRef<HTMLDivElement | null>(null);
	if (
		isPOBannerPlacementforQFEnabled &&
		!isHeadlessQuickFindBannerEnabled &&
		document &&
		!bannerPortalContainerRef.current
	) {
		bannerPortalContainerRef.current = document.createElement('div');
		bannerPortalContainerRef.current.style.display = 'none';
	}

	const onBannerMessageViewed = useCallback((trackImpression: () => void) => {
		trackImpressionRef.current = trackImpression;
	}, []);

	const [isInteracted, setIsInteracted] = useState(false);

	const onMouseEnter = useCallback(() => setIsInteracted(true), []);
	const onFocus = useCallback(() => setIsInteracted(true), []);

	useSearchDialogPreload(isInteracted);

	const content = (
		<>
			<QuickFindResults />
			<Box
				xcss={[textFieldBoxStyles, setMinWidthStyles && minWidthStyles]}
				ref={inputBoxRef}
				onMouseEnter={onMouseEnter}
				onFocus={onFocus}
			>
				<KeyboardHighlightProvider listenerNode={effectiveInputRef?.current}>
					<SearchTextField
						dialogId={dialogId}
						inputRef={effectiveInputRef}
						localQuery={localQuery}
						setLocalQuery={setLocalQuery}
						textfieldComponent={textfieldComponent}
						postQueryDisabled={postQueryDisabled}
						customPlaceholder={customPlaceholder}
						onGhostTextGenerated={onGhostTextGenerated}
						intentRouterEnabled={intentRouterEnabled}
						onPrefetch={onPrefetch}
					/>
					{!isSSR && (
						<FlagsProvider>
							{isPOBannerPlacementforQFEnabled && !isHeadlessQuickFindBannerEnabled && (
								<QuickFindBannerPortal
									onBannerMessageViewed={onBannerMessageViewed}
									isInteracted={isInteracted}
								/>
							)}
							{quickFindOpen && (
								<WidthObserverProvider>
									<SearchDialogAsync
										dialogId={dialogId}
										dialogRef={dialogRef}
										inputRef={effectiveInputRef}
										localQuery={localQuery}
										setLocalQuery={setLocalQuery}
										PlacementPO={PlacementPO}
										showFilters={showFilters}
										searchRefreshStyling={searchRefreshStyling}
										preQueryContent={preQueryContent}
									/>
								</WidthObserverProvider>
							)}
						</FlagsProvider>
					)}
					{showFiltersRow && <FiltersRow />}
				</KeyboardHighlightProvider>
			</Box>
		</>
	);

	if (isPOBannerPlacementforQFEnabled) {
		const contentWithHeadlessPlacement = isHeadlessQuickFindBannerEnabled ? (
			<QuickFindHeadlessBannerProvider isInteracted={isInteracted}>
				{content}
			</QuickFindHeadlessBannerProvider>
		) : (
			content
		);

		return (
			<PlacementPortalContainerProvider value={bannerPortalContainerRef.current}>
				<TrackImpressionProvider value={onBannerVisible}>
					{contentWithHeadlessPlacement}
				</TrackImpressionProvider>
			</PlacementPortalContainerProvider>
		);
	}

	return content;
};

export const QuickFindShell = ({
	children,
	queryParams: actualQueryParams,
	callbacks,
	...props
}: PropsWithChildren<QuickFindProps>): React.JSX.Element => {
	const { onSearchCompleted } = callbacks ?? {};
	const customParams = props.primaryProduct?.includes(ProductKeys.Jira)
		? { [REFERRER_QUERY_PARAM]: SearchExperience.QUICK_FIND }
		: {};
	/**
	 * Quick Find does not need to set queryParams, hence using an object
	 * in a state variable as the queryParams object. Any interactions with
	 * the url should be done through onNavigate in the AppContext.
	 */
	const [queryParams, setQueryParams] = useState<QueryParams>({
		text: actualQueryParams?.text,
		...customParams,
	});

	/**
	 * Quick Find only reacts to changes in the text query parameter when the
	 * user is on the search page.
	 */
	const previousTextQueryParam = useRef<string | undefined>(actualQueryParams?.text);
	useEffect(() => {
		if (actualQueryParams?.text === previousTextQueryParam.current) {
			return;
		}

		// If isOnSearchRoute fails and returns undefined, we default to true
		const onSearchRoute = isOnSearchRoute(props.generateSearchUrl) ?? true;
		if (onSearchRoute) {
			setQueryParams((previousQueryParams) => ({
				...previousQueryParams,
				text: actualQueryParams?.text,
			}));
		}

		previousTextQueryParam.current = actualQueryParams?.text;
	}, [actualQueryParams?.text, props.generateSearchUrl]);

	return (
		<BootstrapProvider
			{...props}
			queryParams={queryParams}
			callbacks={{ onSearchCompleted, updateQueryParameters: setQueryParams }}
			statsigLayer={STATSIG_QUICK_FIND_LAYER}
			experience={SearchExperience.QUICK_FIND}
		>
			<SearchStoreProvider experience={SearchExperience.QUICK_FIND}>
				<QuickFindStoreProvider>
					<QuickFindPostQueryVitalStatsProvider>
						<IntentDetectionVitalStatsProvider>
							<QuickFindAppContextProvider {...props} queryParams={queryParams}>
								{children}
							</QuickFindAppContextProvider>
						</IntentDetectionVitalStatsProvider>
					</QuickFindPostQueryVitalStatsProvider>
				</QuickFindStoreProvider>
			</SearchStoreProvider>
		</BootstrapProvider>
	);
};

/**
 * Quick-Find–scoped `AppContextProvider`. Wraps the host-provided `onNavigate`
 * with a warm-up that primes the search-page query cache in the backend when the
 * user navigates to the search page from Quick Find.
 */
const QuickFindAppContextProvider = ({
	children,
	...props
}: PropsWithChildren<AppContextProviderProps>): React.JSX.Element => {
	const wrappedOnNavigate = useQuickFindOnNavigateWithWarmup(
		props.onNavigate,
		props.generateSearchUrl,
	);

	return (
		<AppContextProvider {...props} onNavigate={wrappedOnNavigate}>
			{children}
		</AppContextProvider>
	);
};

export const QuickFind = ({
	// eslint-disable-next-line no-unused-vars
	children,
	...props
}: PropsWithChildren<QuickFindProps>): React.JSX.Element => {
	return (
		<UFOSegment name="quick-find">
			<MessagesIntlProvider experience={SearchExperience.QUICK_FIND}>
				<RelayEnvironmentProvider>
					<QuickFindShell {...props}>
						<QuickFindComponent {...props} />
					</QuickFindShell>
				</RelayEnvironmentProvider>
			</MessagesIntlProvider>
		</UFOSegment>
	);
};
